import { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react'
import type { ReactNode } from 'react'
import { apiGet, apiPost, tokenStore } from '@shared/libs/apiClient'
import type { AppUser, LoginResponse, RegisterResponse, UserRole } from '@shared/interfaces/appInterface'

interface RegisterInput {
    name: string
    email: string
    password: string
    role: 'author' | 'reviewer'
    phone?: string
    institution?: string
    department?: string
    specialization?: string
}

interface AuthState {
    user: AppUser | null
    loading: boolean
    bootstrapping: boolean
    login: (email: string, password: string) => Promise<AppUser>
    register: (input: RegisterInput) => Promise<AppUser>
    logout: () => void
    refreshUser: () => Promise<void>
}

const AuthContext = createContext<AuthState | null>(null)
const USER_KEY = 'cspg.user'

export function AuthProvider({ children }: { children: ReactNode }) {
    const [user, setUser] = useState<AppUser | null>(() => {
        const raw = localStorage.getItem(USER_KEY)
        return raw ? (JSON.parse(raw) as AppUser) : null
    })
    const [loading, setLoading] = useState(false)
    const [bootstrapping, setBootstrapping] = useState(true)

    useEffect(() => {
        if (user) localStorage.setItem(USER_KEY, JSON.stringify(user))
        else localStorage.removeItem(USER_KEY)
    }, [user])

    useEffect(() => {
        const token = tokenStore.get()
        if (!token) {
            setBootstrapping(false)
            return
        }
        let cancelled = false
        ;(async () => {
            try {
                const r = await apiGet<{ success: boolean; user: AppUser }>('/auth/me')
                if (!cancelled && r.user) setUser(r.user)
            } catch {
                if (!cancelled) {
                    tokenStore.clear()
                    setUser(null)
                }
            } finally {
                if (!cancelled) setBootstrapping(false)
            }
        })()
        return () => {
            cancelled = true
        }
    }, [])

    const login = useCallback(async (email: string, password: string) => {
        setLoading(true)
        try {
            const r = await apiPost<LoginResponse>('/auth/login', { email, password })
            tokenStore.set(r.token)
            setUser(r.user)
            return r.user
        } finally {
            setLoading(false)
        }
    }, [])

    const register = useCallback(async (input: RegisterInput) => {
        setLoading(true)
        try {
            const r = await apiPost<RegisterResponse>('/auth/register', input)
            tokenStore.set(r.token)
            setUser(r.user)
            return r.user
        } finally {
            setLoading(false)
        }
    }, [])

    const logout = useCallback(() => {
        tokenStore.clear()
        setUser(null)
    }, [])

    const refreshUser = useCallback(async () => {
        try {
            const r = await apiGet<{ success: boolean; user: AppUser }>('/auth/me')
            if (r.user) setUser(r.user)
        } catch {
        }
    }, [])

    const value = useMemo<AuthState>(
        () => ({ user, loading, bootstrapping, login, register, logout, refreshUser }),
        [user, loading, bootstrapping, login, register, logout, refreshUser]
    )

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
    const ctx = useContext(AuthContext)
    if (!ctx) throw new Error('useAuth must be used inside AuthProvider')
    return ctx
}

export function dashboardHomeFor(role: UserRole): string {
    if (role === 'admin' || role === 'editor') return '/dashboard/admin'
    if (role === 'reviewer') return '/dashboard/reviewer'
    return '/dashboard/author'
}
